<template>
    <div class="bg-white rounded-xl shadow-lg p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
            <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="text-blue-600 mr-2" />
            {{ regionName }} Statistics
        </h3>

        <!-- Region Overview Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <!-- Total Expected Graduates -->
            <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-blue-100 text-sm font-medium">Total Expected Graduates</p>
                        <p class="text-2xl font-bold mt-1">{{ formatNumber(totalGraduates) }}</p>
                        <p class="text-blue-100 text-xs mt-1">Fatima College of Health Sciences</p>
                    </div>
                    <div class="bg-blue-400 bg-opacity-30 rounded-full p-2">
                        <font-awesome-icon :icon="['fas', 'graduation-cap']" class="text-lg" />
                    </div>
                </div>
            </div>

            <!-- Female Graduates -->
            <div class="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-4 text-white">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-pink-100 text-sm font-medium">Female Graduates</p>
                        <p class="text-2xl font-bold mt-1">{{ formatNumber(femaleGraduates) }}</p>
                        <p class="text-pink-100 text-xs mt-1">{{ ((femaleGraduates / totalGraduates) * 100).toFixed(1)
                            }}% of total</p>
                    </div>
                    <div class="bg-pink-400 bg-opacity-30 rounded-full p-2">
                        <font-awesome-icon :icon="['fas', 'female']" class="text-lg" />
                    </div>
                </div>
            </div>

            <!-- Male Graduates -->
            <div class="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-indigo-100 text-sm font-medium">Male Graduates</p>
                        <p class="text-2xl font-bold mt-1">{{ formatNumber(maleGraduates) }}</p>
                        <p class="text-indigo-100 text-xs mt-1">{{ ((maleGraduates / totalGraduates) * 100).toFixed(1)
                            }}% of total</p>
                    </div>
                    <div class="bg-indigo-400 bg-opacity-30 rounded-full p-2">
                        <font-awesome-icon :icon="['fas', 'male']" class="text-lg" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Program Distribution Chart -->
        <div class="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <font-awesome-icon :icon="['fas', 'chart-pie']" class="text-blue-600 mr-2" />Program Distribution
            </h3>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <!-- ApexCharts Donut Chart -->
                <div class="flex justify-center">
                    <apexchart type="donut" :options="chartOptions" :series="chartSeries" height="300"></apexchart>
                </div>

                <!-- Legend -->
                <div class="space-y-3 max-h-80 overflow-y-auto">
                    <div v-for="program in programData" :key="program.name"
                        class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div class="flex items-center space-x-3">
                            <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: program.color }"></div>
                            <div>
                                <p class="font-medium text-sm text-gray-800">{{ program.name }}</p>
                                <p class="text-xs text-gray-500">{{ ((program.count / totalGraduates) * 100).toFixed(1)
                                }}% of total</p>
                            </div>
                        </div>
                        <div class="text-right">
                            <p class="font-bold text-gray-800">{{ program.count }}</p>
                            <p class="text-xs text-gray-500">students</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div class="bg-blue-50 rounded-lg p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-blue-800">Largest Program</p>
                            <p class="text-lg font-bold text-blue-900">{{ largestProgram.name }}</p>
                        </div>
                        <div class="bg-blue-500 rounded-full p-2">
                            <font-awesome-icon :icon="['fas', 'crown']" class="text-white text-sm" />
                        </div>
                    </div>
                </div>

                <div class="bg-green-50 rounded-lg p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-green-800">Programs Offered</p>
                            <p class="text-lg font-bold text-green-900">{{ programData.length }}</p>
                        </div>
                        <div class="bg-green-500 rounded-full p-2">
                            <font-awesome-icon :icon="['fas', 'list']" class="text-white text-sm" />
                        </div>
                    </div>
                </div>

                <div class="bg-purple-50 rounded-lg p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-purple-800">Average per Program</p>
                            <p class="text-lg font-bold text-purple-900">{{ averagePerProgram }}</p>
                        </div>
                        <div class="bg-purple-500 rounded-full p-2">
                            <font-awesome-icon :icon="['fas', 'calculator']" class="text-white text-sm" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sponsor Distribution and Nationality Charts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Sponsor Distribution -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <font-awesome-icon :icon="['fas', 'handshake']" class="text-blue-600 mr-2" />
                    Sponsor Distribution
                </h3>

                <div class="space-y-4 max-h-96 overflow-y-auto">
                    <div v-for="sponsor in sponsorData" :key="sponsor.name"
                        class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div class="flex items-center space-x-3 flex-1">
                            <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: sponsor.color }"></div>
                            <div class="min-w-0 flex-1">
                                <p class="font-medium text-sm text-gray-800 truncate">{{ sponsor.name }}</p>
                                <p class="text-xs text-gray-500">{{ ((sponsor.count / totalGraduates) * 100).toFixed(1)
                                    }}%</p>
                            </div>
                        </div>
                        <div class="text-right ml-4">
                            <p class="font-bold text-gray-800">{{ sponsor.count }}</p>
                            <p class="text-xs text-gray-500">students</p>
                        </div>
                    </div>
                </div>

                <!-- Summary -->
                <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-blue-800">Total Students</p>
                            <p class="text-lg font-bold text-blue-900">{{ formatNumber(totalGraduates) }}</p>
                        </div>
                        <div class="bg-blue-500 rounded-full p-2">
                            <font-awesome-icon :icon="['fas', 'users']" class="text-white text-sm" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Top Nationalities -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <font-awesome-icon :icon="['fas', 'globe']" class="text-green-600 mr-2" />
                    Top {{ nationalityData.length }} Nationalities
                </h3>

                <div class="space-y-4">
                    <div v-for="nationality in nationalityData" :key="nationality.name"
                        class="flex items-center space-x-3">
                        <div class="w-10 h-10 flex items-center justify-center bg-white border rounded-full">
                            <span class="text-2xl">{{ getFlag(nationality.name) }}</span>
                        </div>
                        <div class="flex-1">
                            <div class="flex justify-between text-sm mb-1">
                                <span class="font-medium truncate">{{ nationality.name }}</span>
                                <span class="font-bold">{{ nationality.count }}</span>
                            </div>
                            <div class="w-full bg-gray-200 rounded-full h-3">
                                <div class="bg-green-500 h-3 rounded-full transition-all duration-1000"
                                    :style="{ width: `${((nationality.count / totalGraduates) * 100).toFixed(1)}%` }">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Summary -->
                <div class="mt-6 p-4 bg-green-50 rounded-lg">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-green-800">Dominant Nationality</p>
                            <p class="text-lg font-bold text-green-900">{{ dominantNationality.name }} ({{
                                dominantNationality.percentage }}%)</p>
                        </div>
                        <div class="bg-green-500 rounded-full p-2">
                            <font-awesome-icon :icon="['fas', 'star']" class="text-white text-sm" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getCountryFlag } from '../../../helpers/countryFlags';

export default {
    name: 'RegionStatisticsChart',
    props: {
        regionName: {
            type: String,
            required: true
        },
        totalGraduates: {
            type: Number,
            required: true
        },
        femaleGraduates: {
            type: Number,
            required: true
        },
        maleGraduates: {
            type: Number,
            required: true
        },
        programData: {
            type: Array,
            required: true
        },
        sponsorData: {
            type: Array,
            required: true
        },
        nationalityData: {
            type: Array,
            required: true
        }
    },
    computed: {
        largestProgram() {
            if (this.programData.length === 0) {
                return { name: 'N/A', count: 0 };
            }
            return this.programData.reduce((largest, current) =>
                current.count > largest.count ? current : largest
            );
        },
        averagePerProgram() {
            if (this.programData.length === 0) {
                return 0;
            }
            return Math.round(this.totalGraduates / this.programData.length);
        },
        dominantNationality() {
            if (this.nationalityData.length > 0) {
                const dominant = this.nationalityData[0];
                return {
                    name: dominant.name,
                    percentage: ((dominant.count / this.totalGraduates) * 100).toFixed(1)
                };
            }
            return { name: 'N/A', percentage: 0 };
        },
        chartSeries() {
            if (this.programData.length === 0) {
                return [];
            }
            return this.programData.map(program => program.count);
        },
        chartOptions() {
            return {
                chart: {
                    type: 'donut',
                    height: 300,
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800,
                        animateGradually: {
                            enabled: true,
                            delay: 150
                        },
                        dynamicAnimation: {
                            enabled: true,
                            speed: 350
                        }
                    }
                },
                labels: this.programData.length > 0 ? this.programData.map(program => program.name) : [],
                colors: this.programData.length > 0 ? this.programData.map(program => program.color) : [],
                plotOptions: {
                    pie: {
                        donut: {
                            size: '65%',
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    fontSize: '16px',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 600,
                                    color: '#374151'
                                },
                                value: {
                                    show: true,
                                    fontSize: '20px',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 700,
                                    color: '#1F2937',
                                    formatter: function (val) {
                                        return val;
                                    }
                                },
                                total: {
                                    show: true,
                                    label: 'Total',
                                    fontSize: '14px',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    formatter: function (w) {
                                        return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                    }
                                }
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    },
                },
                legend: {
                    show: false
                },
                // tooltip: {
                //     enabled: true,
                //     theme: 'light',
                //     style: {
                //         fontSize: '12px'
                //     },
                //     y: {
                //         formatter: function (value, opts) {
                //             const seriesIndex = opts.seriesIndex;
                //             const w = opts.w;
                //             const program = w.config.labels[seriesIndex];
                //             const total = w.globals.seriesTotals.reduce(function (a, b) {
                //                 return a + b;
                //             }, 0);
                //             const percentage = ((value / total) * 100).toFixed(1);
                //             return program + ': ' + value + ' graduates (' + percentage + '%)';
                //         }
                //     }
                // },
                responsive: [
                    {
                        breakpoint: 768,
                        options: {
                            chart: {
                                height: 250
                            },
                            plotOptions: {
                                pie: {
                                    donut: {
                                        size: '60%'
                                    }
                                }
                            }
                        }
                    }
                ]
            };
        }
    },
    methods: {
        formatNumber(num) {
            return num.toLocaleString();
        },
        getFlag(country) {
            return getCountryFlag(country);
        },
    }
};
</script>

<style scoped>
/* Custom scrollbar for the legend */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>